import { devices_config } from '@devices/index';
import { expandLabelConfig, getPreset } from 'src/configs/dash-device-labels/presets';
import { resolveDashInfoLabelsConfig } from 'src/configs/dash-device-labels/resolve_config';
import { HALF_SLEEPING_TYPES, SLEEPING_TYPES } from 'src/constants';
import { mapIconSolidWeightToLight } from 'src/helpers/new-layout-helpers';
import {
	getIsCalibrationFromstatus,
	getIsDimmerCalibratingFromStatus,
} from 'src/helpers/interface_helpers';
import { getCustomizeLabelList } from 'src/components/DevicesSettings/CustomizeDeviceWizard/customize-device-wizard-helpers';
import {
	countGatewayProxiedDevices,
	formatGatewayProxiedDevicesLabel,
	isDashGatewayDevice,
} from 'src/helpers/dash_device_gateway_labels';
import { isVirtualDevice } from 'src/helpers/virtual_helpers/virtual_components_helpers';
import type { Device } from 'src/types/Device';
import type { LabelV2PropsTypes } from '@Shelly/shelly-design-system';

export type DashDeviceInfoLabelItem = {
	value: LabelV2PropsTypes['value'];
	icon?: LabelV2PropsTypes['icon'];
	iconColor?: LabelV2PropsTypes['iconColor'];
	iconStyle?: LabelV2PropsTypes['iconStyle'];
	name?: string;
	textClass?: string;
	noBackground?: boolean;
	noPadding?: boolean;
};

export type ResolveDashDeviceInfoLabelsInput = {
	device: Device | undefined | null;
	statuses: any[] | undefined;
	status?: any;
	settings?: any;
	offline?: boolean;
	isUpdating?: boolean;
	notAuthenticated?: boolean;
	isDefaultState?: boolean;
	hasCriticalNotification?: boolean;
	hasTimer?: boolean;
	/** Formatted countdown (e.g. `01:19:06`) — appended after status chips when allowed. */
	timerCountdown?: string;
};

export type ResolveDashDeviceInfoLabelsResult = {
	showInfoSlot: boolean;
	labels: DashDeviceInfoLabelItem[];
	/** True when timer was placed in the info row (caller should omit it from additionalText). */
	timerInInfoSlot: boolean;
	/**
	 * When set, dash cards should show this LabelV2 in the right slot instead of
	 * a customized statusLabelParam / empty EM control branch (e.g. power for EMs).
	 */
	rightSlotLabel: DashDeviceInfoLabelItem | null;
	/**
	 * Extracted virtuals without a custom priority sequence: all metrics live in the
	 * info row — suppress VirtualDeviceControl's single mainLabel in the right slot.
	 */
	suppressVirtualMainLabel: boolean;
};

const MAX_VISIBLE_INFO_LABELS = 3;

/** Same rule as `isBluTRV` — kept local to avoid heavy interface_helpers mock coupling in tests. */
const isBluTrvType = (deviceType: string | undefined): boolean =>
	typeof deviceType === 'string' && /^SBTR-001AEU/.test(deviceType);

export const statusMatchesKey = (status: any, key: string): boolean => {
	if (!status || !key) return false;
	return status.name === key || status.role === key;
};

const mapStatusToInfoLabel = (status: any): DashDeviceInfoLabelItem | null => {
	if (!status) return null;

	const value =
		status.value === 0 || status.value
			? String(status.value)
			: status.value === '' || status.value == null
				? undefined
				: String(status.value);

	if (value == null && !status.icon && !status.dotsType) return null;

	return {
		name: status.name,
		value,
		icon: mapIconSolidWeightToLight(status.icon),
		// Face color is owned by DashDeviceWidget (active white → dark; glass → white).
		// Do not forward status iconColor — it is usually light and fights the active face.
		noBackground: true,
		noPadding: true,
	};
};

const isCalibrating = (device: Device, status: any): boolean =>
	!!status &&
	(getIsCalibrationFromstatus(device, status) || getIsDimmerCalibratingFromStatus(device, status));

const hasCustomizedStatusPriorities = (device: Device): boolean => {
	const param = device?.statusLabelParam;
	if (!param) return false;
	if (Array.isArray(param)) return param.length > 0;
	return Object.keys(param).length > 0;
};

/**
 * Battery-powered device types for dash sane defaults.
 * Source lists: `SLEEPING_TYPES` / `HALF_SLEEPING_TYPES` in `src/constants.ts`,
 * plus BLU (`gen === 'GBLE'`), Blu TRV, and extracted virtuals (often expose battery).
 */
export const isDashBatteryPoweredDevice = (
	device: { type?: string; gen?: string | number } | null | undefined,
): boolean => {
	if (!device?.type) return false;
	if (device.gen === 'GBLE') return true;
	if (isBluTrvType(device.type)) return true;
	if (isVirtualDevice(device.type)) return true;
	if (SLEEPING_TYPES.includes(device.type)) return true;
	if (HALF_SLEEPING_TYPES.includes(device.type)) return true;
	return false;
};

export const isBatteryStatusLabel = (
	status: { name?: string; role?: string } | null | undefined,
): boolean => status?.name === 'battery' || status?.role === 'battery';

/** Drop battery from sane-default dash chips (Customize Card can still opt in). */
export const omitBatteryFromDashSaneDefaults = (
	statuses: any[],
	device: Device | undefined | null,
	hasCustomPriorities: boolean,
): any[] => {
	if (hasCustomPriorities || !isDashBatteryPoweredDevice(device)) {
		return statuses;
	}
	return (statuses || []).filter((status) => !isBatteryStatusLabel(status));
};

/** Non-control virtual statuses suitable for the horizontal info row. */
export const listVirtualStatusesForInfoRow = (statuses: any[]): any[] =>
	(statuses || []).filter(
		(label) =>
			label &&
			label.name !== undefined &&
			!label.hideLabel &&
			!label.advLabel &&
			!label.hasButton &&
			label.vcType !== 'button' &&
			!(label.vcType === 'boolean' && label.vcView === 'toggle') &&
			!/^(timer|schedule|last)$/.test(label.name || ''),
	);

/**
 * Extracted virtuals with no dashInfoLabels preset and no Customize Card priorities:
 * list every metric in the info row (do not address a single main slot).
 */
export const shouldListAllVirtualStatusesInInfoRow = (
	device: Device | undefined | null,
	hasDedicatedLabelSequence: boolean,
): boolean => {
	if (!device || !isVirtualDevice(device.type)) return false;
	if (hasDedicatedLabelSequence) return false;
	if (hasCustomizedStatusPriorities(device)) return false;
	if (device.mainActionType === 'param') return false;
	return true;
};

/**
 * Blocking device states hide parameter labels (mutually exclusive with live metrics).
 * Timers stack with labels and are not blocking.
 */
export const shouldShowDashDeviceInfoLabels = (input: ResolveDashDeviceInfoLabelsInput): boolean => {
	const {
		device,
		status,
		offline,
		isUpdating,
		notAuthenticated,
		isDefaultState,
		hasCriticalNotification,
		hasTimer,
	} = input;

	if (!device) return false;
	if (offline) return false;
	if (isUpdating) return false;
	if (notAuthenticated) return false;
	if (isCalibrating(device, status)) return false;
	if (hasCriticalNotification) return false;
	if (isDefaultState === false && !hasTimer) return false;

	return true;
};

/** Timer chip may show whenever the device is online and not in a hard blocking state. */
export const shouldShowDashDeviceTimerLabel = (input: ResolveDashDeviceInfoLabelsInput): boolean => {
	const { device, status, offline, isUpdating, notAuthenticated, hasTimer, timerCountdown } = input;

	if (!device || !hasTimer || !timerCountdown) return false;
	if (offline || isUpdating || notAuthenticated) return false;
	if (isCalibrating(device, status)) return false;

	return true;
};

/**
 * Resolve ordered LabelV2-shaped chips for the DashDeviceWidget `info` slot.
 * Timer countdown (when present) is appended after status metrics so it sits
 * to the right of power/etc. in the horizontal row.
 *
 * For presets with `promoteFirstToRightSlot` (e.g. emeter), the first matched
 * status becomes `rightSlotLabel` (main) and only the rest go into `labels`.
 */
export const resolveDashDeviceInfoLabels = (
	input: ResolveDashDeviceInfoLabelsInput,
): ResolveDashDeviceInfoLabelsResult => {
	const empty: ResolveDashDeviceInfoLabelsResult = {
		showInfoSlot: false,
		labels: [],
		timerInInfoSlot: false,
		rightSlotLabel: null,
		suppressVirtualMainLabel: false,
	};

	const showMetrics = shouldShowDashDeviceInfoLabels(input);
	const showTimer = shouldShowDashDeviceTimerLabel(input);

	if (!showMetrics && !showTimer) {
		return empty;
	}

	const { device, statuses, status, settings } = input;
	const statusList = Array.isArray(statuses) ? statuses : [];
	const config = showMetrics ? resolveDashInfoLabelsConfig(devices_config, device) : undefined;
	const preset = getPreset(config);
	const requestedKeys = showMetrics ? expandLabelConfig(config) : [];
	const listAllVirtualStatuses = shouldListAllVirtualStatusesInInfoRow(
		device,
		requestedKeys.length > 0,
	);
	const customPriorities = device ? hasCustomizedStatusPriorities(device as Device) : false;

	let matched: any[] = [];

	if (showMetrics) {
		if (requestedKeys.length > 0) {
			for (const key of requestedKeys) {
				const found = statusList.find(
					(item) =>
						statusMatchesKey(item, key) &&
						!item.hideLabel &&
						!item.advLabel &&
						!/^(timer|schedule|last)$/.test(item.name || ''),
				);
				if (found && !matched.some((item) => item === found || item.name === found.name)) {
					matched.push(found);
				}
			}
		} else if (listAllVirtualStatuses) {
			matched = listVirtualStatusesForInfoRow(statusList);
		} else {
			matched = getCustomizeLabelList(device as Device, statusList);
		}

		matched = omitBatteryFromDashSaneDefaults(matched, device, customPriorities);
	}

	const mapped = matched
		.map(mapStatusToInfoLabel)
		.filter((item): item is DashDeviceInfoLabelItem => !!item);

	let rightSlotLabel: DashDeviceInfoLabelItem | null = null;
	let infoMapped = mapped;

	if (preset?.promoteFirstToRightSlot && mapped.length > 0) {
		rightSlotLabel = mapped[0];
		infoMapped = mapped.slice(1);
	} else if (
		showMetrics &&
		device &&
		isDashGatewayDevice(device) &&
		!customPriorities &&
		!rightSlotLabel
	) {
		const count = countGatewayProxiedDevices(status, settings);
		rightSlotLabel = formatGatewayProxiedDevicesLabel(count);
	}

	const labels = listAllVirtualStatuses ? infoMapped : infoMapped.slice(0, MAX_VISIBLE_INFO_LABELS);

	if (showTimer && input.timerCountdown) {
		labels.push({
			name: 'timer',
			value: input.timerCountdown,
			icon: mapIconSolidWeightToLight('far fa-clock'),
			noBackground: true,
			noPadding: true,
		});
	}

	return {
		showInfoSlot: labels.length > 0,
		labels,
		timerInInfoSlot: showTimer,
		rightSlotLabel,
		suppressVirtualMainLabel: listAllVirtualStatuses,
	};
};
